import {
  PrimaryButton,
  SecondaryButton,
} from "@graffer-inc/console-design-kit";
import classnames from "classnames";
import FileSaver from "file-saver";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import apiClient from "../../../../application/apis/tenant-management/tenant-management-client";
import { ProductNameMap } from "../../../../application/domain/organization/org-product";
import { ProductDocument } from "../../../../application/domain/product-docs";
import { reportUnexpectedError } from "../../../../error-reporter";
import { StaticFeatureFlags } from "../../../../feature-flags";
import { useModal } from "../../../../utils/custom-hooks";
import { formatDatetime } from "../../../../utils/date-utils";
import { BaseModal } from "../../../atoms/base-modal";
import { ServiceProviderTemplate } from "../../../template/service-provider/default-template";
import { ServiceProviderR } from "../routes";

export const ProductDocsPage: React.FC = () => {
  const allProducts = ["SmartApply", "BoothReserve", "Portal", "Call"] as const; // TODO add other products when their documents are ready
  const maxFileMB = 5;
  const [savedDocs, setSavedDocs] = useState<Array<ProductDocument>>([]);
  const [targetPdt, setTargetPdt] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState<{ name: string; data: string }>();
  const uploadDocModal = useModal();

  const fetchDocs = useCallback(async () => {
    try {
      const { data } = await apiClient.fetchPdtDocs();
      setSavedDocs(data || []);
    } catch (e) {
      reportUnexpectedError(e);
    }
  }, [setSavedDocs]);
  const downloadDoc = async (pdtCode: string, docName: string) => {
    const { data } = await apiClient.downloadProductDocFile(pdtCode);
    FileSaver.saveAs(new Blob([data]), docName);
  };
  const uploadDoc = async () => {
    try {
      setIsLoading(true);
      await apiClient.uploadProductDocFile(targetPdt!, file!.name, file!.data);
      toast.success("保存しました");
      fetchDocs();
      uploadDocModal.close();
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("失敗しました. " + e.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const docs: Array<{
    productCode: string;
    productName: string;
    docName?: string;
    updatedAt?: Date;
  }> = allProducts.map((p) => {
    const saved: ProductDocument | undefined = savedDocs.find(
      (d) => d.productCode === p,
    );
    return {
      productCode: p,
      productName: ProductNameMap[p],
      docName: saved?.docName,
      updatedAt: saved?.updatedAt,
    };
  });

  return (
    <ServiceProviderTemplate
      breadcrumbs={[
        { to: ServiceProviderR.ProductDocs.link({}), title: "マニュアル設定" },
      ]}
    >
      <nav className="level">
        <div className="level-left">
          <div className="level-item">
            <h1 className="is-size-2">マニュアル設定</h1>
          </div>
        </div>
      </nav>
      {StaticFeatureFlags.IS_SANDBOX ? (
        <article className="message is-warning">
          <div className="message-body">
            サンドボックス環境ではマニュアルをアップロードすることはできません。
          </div>
        </article>
      ) : (
        <div className="table-container">
          <table className="table is-fullwidth">
            <thead>
              <tr>
                <th>プロダクト名</th>
                <th>ファイル</th>
                <th>最終アップロード日時</th>
                <th aria-label="アップロード"></th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.productCode} className="c-tr-has-tag">
                  <td>{d.productName}</td>
                  <td>
                    {d.docName ? (
                      <>
                        <span className="mr-4">{d.docName}</span>
                        <SecondaryButton
                          text={"ダウンロード"}
                          onClick={async (e) => {
                            e.preventDefault();
                            await downloadDoc(d.productCode, d.docName!);
                          }}
                        />
                      </>
                    ) : (
                      <span>未設定</span>
                    )}
                  </td>
                  <td>{d.updatedAt && formatDatetime(d.updatedAt)}</td>
                  <td>
                    <PrimaryButton
                      text={"マニュアルアップロード"}
                      onClick={() => {
                        setTargetPdt(d.productCode);
                        uploadDocModal.open();
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <BaseModal
        title={"マニュアルをアップロードする"}
        isActive={uploadDocModal.isActive}
        onClose={uploadDocModal.close}
      >
        <section className="modal-card-body">
          <article className="message is-warning">
            <div className="message-body">
              マニュアルのファイルを登録・更新すると、テナントユーザーがダウンロードできるファイルも同時に更新されます。
              <br />
              ファイルの中身はお間違えないよう、十分にお気をつけください。
            </div>
          </article>
          <div className="file has-name">
            <label className="file-label">
              <input
                className="file-input"
                type="file"
                name="resume"
                accept="application/pdf"
                onChange={(e: any) => {
                  const fileReader = new FileReader();
                  if (e.target.files.length === 0) {
                    // file選択せずにキャンセルしたパターン
                    return;
                  }
                  const fileDropped = e.target.files[0];
                  if (fileDropped.size / 1024 / 1024 > maxFileMB) {
                    toast.error(
                      `${maxFileMB}MB以上のファイルは登録いただけません。`,
                    );
                    return;
                  }
                  fileReader.onload = function () {
                    const dataUri = this.result as string;
                    setFile({
                      name: fileDropped.name,
                      data: dataUri.split(",")[1],
                    });
                  };
                  fileReader.readAsDataURL(fileDropped);
                }}
              />
              <span className="file-cta">
                <span className="file-icon">
                  <i className="fas fa-upload" />
                </span>
                <span className="file-label">
                  {!file ? "選択してください" : "再選択する"}
                </span>
              </span>
              {file && <span className="file-name">{file.name}</span>}
            </label>
          </div>
        </section>
        <footer className="modal-card-foot">
          <button
            className="button is-failure is-fullwidth"
            onClick={uploadDocModal.close}
          >
            キャンセル
          </button>
          <button
            className={classnames("button is-info is-fullwidth", {
              disabled: !file,
            })}
            disabled={!file || isLoading}
            onClick={uploadDoc}
          >
            登録する
          </button>
        </footer>
      </BaseModal>
    </ServiceProviderTemplate>
  );
};
